// Route: /library — Библиотека (LEARNING-SYSTEM-UI §2).
// Единый экран источников: фильтры «Все / Не в курсе / Без папки», рельс папок,
// мульти-выбор с bulk-операциями «В курс» / «В папку» / удалить.
//
// Заменяет /materials и /collections (оба редиректятся сюда).
// URL state: ?filter, ?q, ?type, ?folder, ?demo

import type { MaterialKind } from "@studyhub/shared/contracts/materials";
import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { LIST_PAGE_SHELL } from "@studyhub/ui";
import { Button } from "@studyhub/ui/components/button";
import { SectionHeader } from "@studyhub/ui/components/studyhub/section-header";
import { toast } from "@studyhub/ui/toast";
import { IconFolderOpen, IconLibrary, IconUpload } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
	useBulkDeleteMaterials,
	useCoursesForPicker,
	useLibraryFolders,
	useLibraryInvalidate,
} from "@/components/library/_api";
import { FolderDialog } from "@/components/library/folder-dialog";
import { FOLDER_ALL, FOLDER_NONE } from "@/components/library/folder-rail";
import { type LibraryQueryState, LibraryView } from "@/components/library/library-view";
import { PickMaterialsForFolderDialog } from "@/components/library/pick-materials-for-folder-dialog";
import { SelectionBar } from "@/components/library/selection-bar";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import {
	SectionIntro,
	SectionIntroReopen,
	useSectionIntro,
} from "@/components/shared/section-intro";
import { useConfirm } from "@/hooks/use-confirm";
import { track } from "@/lib/observability/analytics";
import { api } from "@/lib/trpc-client";
import { useUploadDialogStore } from "@/stores/upload-dialog";

const searchSchema = z.object({
	filter: z.enum(["all", "not-in-course", "no-folder"]).optional(),
	q: z.string().optional(),
	type: z.string().optional(),
	/** id папки или `__all__`. */
	folder: z.string().optional(),
	view: z.enum(["grid", "list"]).optional(),
	demo: z.enum(["default", "loading", "empty", "error"]).optional(),
});

export const Route = createFileRoute("/library/")({
	validateSearch: tolerantSearch(searchSchema),
	component: LibraryPage,
	pendingComponent: PagePendingFallback,
});

function LibraryPage(): React.ReactElement {
	const { t } = useTranslation("library");
	const intro = useSectionIntro("library");
	const search = Route.useSearch();
	const navigate = useNavigate({ from: Route.fullPath });
	const confirm = useConfirm();
	const invalidate = useLibraryInvalidate();
	const openUpload = useUploadDialogStore((s) => s.open);

	const [selectedIds, setSelectedIds] = useState<string[]>([]);
	/** Режим выбора = есть выбранные. Вход: hover-чекбокс / long-press / Shift·Cmd. */
	const selectionMode = selectedIds.length > 0;
	const [folderDialogOpen, setFolderDialogOpen] = useState(false);
	const [pickExistingOpen, setPickExistingOpen] = useState(false);

	const folders = useLibraryFolders();
	const courses = useCoursesForPicker();
	const bulkDelete = useBulkDeleteMaterials();

	const state: LibraryQueryState = {
		filter: search.filter ?? "all",
		q: search.q ?? "",
		type: search.type as MaterialKind | undefined,
		folder: search.folder ?? FOLDER_ALL,
	};

	const currentFolderId =
		state.folder !== FOLDER_ALL && state.folder !== FOLDER_NONE && !state.folder.startsWith("__")
			? state.folder
			: null;
	// Имя нужно в подписи кнопки: «Открыть папку “X”» честнее безликого
	// «Открыть папку», когда на экране уже отфильтрованный список.
	const currentFolder = currentFolderId
		? (folders.items.find((f) => f.id === currentFolderId) ?? null)
		: null;

	const openUploadForLibrary = useCallback(() => {
		openUpload(currentFolderId ? { initialFolderId: currentFolderId } : undefined);
	}, [currentFolderId, openUpload]);

	const patchState = useCallback(
		(next: Partial<LibraryQueryState>) => {
			void navigate({
				search: (prev) => ({
					...prev,
					filter: (next.filter ?? prev.filter) === "all" ? undefined : (next.filter ?? prev.filter),
					q: (next.q ?? prev.q) || undefined,
					type: "type" in next ? next.type : prev.type,
					folder:
						(next.folder ?? prev.folder) === FOLDER_ALL ? undefined : (next.folder ?? prev.folder),
				}),
				replace: true,
			});
		},
		[navigate],
	);

	const exitSelection = useCallback(() => {
		setSelectedIds([]);
	}, []);

	const bulkAddToCourse = api.library.bulkAddToCourse.useMutation({
		onSuccess: (res, vars) => {
			invalidate();
			track("library_bulk_add", { count: vars.materialIds.length });
			// Обратная ссылка: пользователь всегда видит, КУДА попало (§2.2).
			toast.success(t("selection.addedToCourse", { count: res.added, course: res.courseTitle }), {
				action: {
					label: t("selection.openCourse"),
					onClick: () =>
						void navigate({ to: "/courses/$courseId", params: { courseId: vars.courseId } }),
				},
			});
			exitSelection();
		},
		onError: () => toast.error(t("selection.addToCourseError")),
	});

	const bulkMoveToFolder = api.library.bulkMoveToFolder.useMutation({
		onSuccess: (res) => {
			invalidate();
			toast.success(t("selection.movedToFolder", { count: res.moved }));
			exitSelection();
		},
		onError: () => toast.error(t("selection.moveToFolderError")),
	});

	const handleDelete = useCallback(() => {
		void (async () => {
			const ok = await confirm({
				title: t("selection.delete"),
				description: t("selection.count", { count: selectedIds.length }),
				tone: "destructive",
			});
			if (!ok) return;
			bulkDelete.mutate(selectedIds, {
				onSuccess: () => {
					invalidate();
					exitSelection();
				},
				onError: () => toast.error(t("errors.loadFailed")),
			});
		})();
	}, [bulkDelete, confirm, exitSelection, invalidate, selectedIds, t]);

	const busy = bulkAddToCourse.isPending || bulkMoveToFolder.isPending || bulkDelete.isPending;

	return (
		<div className={LIST_PAGE_SHELL}>
			<SectionHeader
				icon={<IconLibrary />}
				category="time"
				title={t("title")}
				description={t("subtitle")}
				stackActions
				actions={
					<>
						<SectionIntroReopen intro={intro} />
						<Button className="cursor-pointer gap-1.5" onClick={openUploadForLibrary}>
							<IconUpload size={16} stroke={1.75} />
							{t("add")}
						</Button>
					</>
				}
			/>

			{/* Баннер-объяснение раздела: текст из манифеста модуля, состояние
			    «свёрнут» — серверное (см. section-intro.tsx). */}
			<SectionIntro intro={intro} />

			{/* Выбор папки в рельсе — это ФИЛЬТР, он никуда не ведёт. До 2026-08-14
			    попасть на страницу папки (где живёт генерация по всей папке) из
			    Библиотеки было нельзя вовсе: ссылок не было, а рельс только
			    сужал список. Баннер появляется ровно в момент намерения — когда
			    папка уже выбрана. */}
			{currentFolder && (
				<div className="px-4 pt-2 lg:px-6">
					<Button
						variant="outline"
						className="h-11 w-full cursor-pointer justify-center gap-2"
						onClick={() =>
							void navigate({
								to: "/library/folders/$folderId",
								params: { folderId: currentFolder.id },
							})
						}
					>
						<IconFolderOpen size={16} stroke={1.75} />
						{t("folders.openBanner", { name: currentFolder.name })}
					</Button>
				</div>
			)}

			<LibraryView
				mode="page"
				state={state}
				onStateChange={patchState}
				selectedIds={selectedIds}
				onSelectedIdsChange={setSelectedIds}
				selectionMode={selectionMode}
				onSelectionModeChange={(next) => {
					if (!next) exitSelection();
				}}
				onUpload={openUploadForLibrary}
				onPickExisting={currentFolderId ? () => setPickExistingOpen(true) : undefined}
				onCreateFolder={() => setFolderDialogOpen(true)}
				demo={search.demo}
			/>

			<SelectionBar
				visible={selectedIds.length > 0}
				count={selectedIds.length}
				courses={courses.items}
				folders={folders.items}
				busy={busy}
				onAddToCourse={(courseId) => bulkAddToCourse.mutate({ materialIds: selectedIds, courseId })}
				onMoveToFolder={(folderId) =>
					bulkMoveToFolder.mutate({ materialIds: selectedIds, folderId })
				}
				onDelete={handleDelete}
				onCancel={exitSelection}
			/>

			<FolderDialog
				open={folderDialogOpen}
				onOpenChange={setFolderDialogOpen}
				onCreated={(id) => patchState({ folder: id })}
			/>

			{currentFolderId ? (
				<PickMaterialsForFolderDialog
					folderId={currentFolderId}
					open={pickExistingOpen}
					onOpenChange={setPickExistingOpen}
					onMoved={() => invalidate()}
				/>
			) : null}
		</div>
	);
}
